import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { WriteModeSwitch } from '@/components/contribute/WriteModeSwitch';
import { TaskHeader, TaskNotice, TaskSessionComplete, TaskShell, fillCopy, kitLang, taskPosition } from '@/features/contribution-kit';
import {
  TranscriptionPhraseActionBar,
  TranscriptionPhraseBatchList,
  TranscriptionPhraseSetup,
  TranscriptionPhraseSourceCard,
  TranscriptionPhraseWriteCard,
  TRANSCRIPTION_PHRASE_PAGE_SIZE,
  phraseLanguageLabels,
  speakContributeReturn,
  transcriptionPhraseCopy,
  useTranscriptionSession,
} from '@/features/transcription-phrase';
import type { SpeakContributeData } from '@/features/transcription-phrase';
import { ValidationGateBanner, ValidationGateBlockedScreen, ValidationGateDialog } from '@/features/validation-gate';
import { OfflineTaskNotice } from '@/features/offline/components/OfflineTaskNotice';

/**
 * "Traduire": write French phrases in the local language. "Enregistrer"
 * also opens it with one phrase in router state (SpeakContributeData) to add
 * or edit a translation, and gets the person back with `returnTo`.
 */
export default function TranscriptionPhrase() {
  const { t, language } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const currentLang = lang || language || 'fr';
  const copy = transcriptionPhraseCopy[kitLang(currentLang)];
  const tr = t as unknown as Record<string, unknown>;
  const s = (key: string) => String(tr[key] ?? '');

  const fromSpeakContributeData = location.state as SpeakContributeData | null | undefined;
  const session = useTranscriptionSession(fromSpeakContributeData, location.state);

  const goHome = () => navigate(`/${currentLang}`);
  const speakReturn = speakContributeReturn(fromSpeakContributeData, currentLang);
  const goBack = speakReturn ? () => navigate(speakReturn.to, { state: speakReturn.state }) : goHome;

  const isFromSpeakContribute = Boolean(fromSpeakContributeData?.fromSpeakContribute);
  const isTranslatingToFrench = Boolean(fromSpeakContributeData?.needsTranslationToFrench);
  const labels = phraseLanguageLabels(fromSpeakContributeData, { french: s('french'), localLanguage: s('localLanguage') });

  if (session.validationGate.isBlocked) {
    return <ValidationGateBlockedScreen />;
  }

  if (!session.sessionStarted) {
    return (
      <TranscriptionPhraseSetup
        t={tr}
        copy={copy}
        title={isTranslatingToFrench ? s('translateToFrench') : s('transcribePhrases')}
        description={
          isTranslatingToFrench
            ? s('translatePhraseToFrenchDesc').replace('{language}', fromSpeakContributeData?.targetLanguage || s('localLanguage'))
            : isFromSpeakContribute
              ? s('translatePhraseFromFrenchDesc')
              : s('translatePhrasesGeneralDesc')
        }
        showLanguageSelect={!isTranslatingToFrench}
        showModeSwitch={!isFromSpeakContribute}
        languages={session.targetLanguages}
        selectedLanguage={session.selectedLanguage}
        onLanguageChange={session.setSelectedLanguage}
        sessionResumeActive={session.sessionResumeActive}
        isLoading={session.isLoading}
        onStart={session.startLinkedSession}
        onBack={goHome}
      />
    );
  }

  if (session.sessionComplete) {
    return (
      <TaskSessionComplete
        title={s('sessionCompleted')}
        description={isFromSpeakContribute ? s('allPhrasesTranslated') : copy.doneDescription}
        stats={[{ label: copy.doneSent, value: session.submittedCount, tone: session.submittedCount > 0 ? 'success' : 'default' }]}
        continueLabel={copy.doneBack}
        onContinue={session.dismissSessionComplete}
        homeLabel={speakReturn ? copy.backToRecording : undefined}
        onHome={goBack}
      />
    );
  }

  const { phrases, currentIndex, page, total, current } = session;
  const position = taskPosition(currentIndex, phrases.length);
  const absoluteIndex = (page - 1) * TRANSCRIPTION_PHRASE_PAGE_SIZE + position.current;
  const languageMeta = session.targetLanguages.find((item) => item.code === session.selectedLanguage);

  return (
    <TaskShell
      width="wide"
      header={
        <div className="space-y-4">
          <TaskHeader
            task="translate"
            index={currentIndex}
            total={phrases.length}
            positionLabel={s('phraseXofY').replace('{current}', String(position.current)).replace('{total}', String(position.total))}
            progress={session.progressPercent}
            itemId={current?.id ?? null}
            onBack={goBack}
            meta={
              <>
                <span>{languageMeta?.displayName || languageMeta?.name || session.selectedLanguage}</span>
                <span>
                  {fillCopy(copy.pageMeta, { page })}
                  {typeof total === 'number' ? ` · ${absoluteIndex} / ${total}` : ''}
                </span>
              </>
            }
          />
          {isFromSpeakContribute ? null : <WriteModeSwitch active="translate" />}
        </div>
      }
      notices={
        <>
          <OfflineTaskNotice />
          <ValidationGateBanner />
        </>
      }
      actionBar={
        current ? (
        <TranscriptionPhraseActionBar
          t={tr}
          copy={copy}
          canGoPrevious={currentIndex > 0}
          hasAudio={Boolean(current?.audioUrl)}
          canSend={current !== null && session.translatedText.trim().length > 0}
          isLoading={session.isLoading}
          isSubmitting={session.isSubmitting}
          onPrevious={session.previous}
          onSkip={session.skipPhrase}
          onSend={session.submitTranslation}
          onTogglePlay={session.playAudio}
        />
        ) : null
      }
    >
      {session.blockedGate && (
        <ValidationGateDialog open onOpenChange={(open) => !open && session.clearBlockedGate()} gate={session.blockedGate} />
      )}

      {current ? (
        <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
          <div className="space-y-5">
            <TranscriptionPhraseSourceCard
              t={tr}
              copy={copy}
              current={current}
              sourceLanguageLabel={labels.source}
              isPlaying={session.isPlaying}
              audioRef={session.audioRef}
              canvasRef={session.canvasRef}
              onPlayAudio={session.playAudio}
              onAudioEnded={session.onAudioEnded}
            />
            {phrases.length > 1 || session.hasMore || page > 1 ? (
              <TranscriptionPhraseBatchList
                t={tr}
                copy={copy}
                phrases={phrases}
                currentIndex={currentIndex}
                completedPhrases={session.completedPhrases}
                page={page}
                hasMore={session.hasMore}
                isLoading={session.isLoading}
                onSelectPhrase={session.selectPhrase}
                onPrevPage={session.prevPage}
                onNextPage={session.nextPage}
              />
            ) : null}
          </div>
          <TranscriptionPhraseWriteCard
            t={tr}
            copy={copy}
            translatedText={session.translatedText}
            onTranslatedTextChange={session.setTranslatedText}
            showKeyboard={session.showKeyboard}
            onToggleKeyboard={() => session.setShowKeyboard(!session.showKeyboard)}
            selectedLanguage={session.selectedLanguage}
            targetLanguageLabel={labels.target}
            isLoading={session.isLoading}
            hasPhrases={phrases.length > 0}
          />
        </div>
      ) : (
        <>
          <TaskNotice tone="info" icon="inbox" title={copy.emptyTitle}>
            {copy.emptyBody}
          </TaskNotice>
          {page > 1 || session.hasMore ? (
            <TranscriptionPhraseBatchList
              t={tr}
              copy={copy}
              phrases={phrases}
              currentIndex={currentIndex}
              completedPhrases={session.completedPhrases}
              page={page}
              hasMore={session.hasMore}
              isLoading={session.isLoading}
              onSelectPhrase={session.selectPhrase}
              onPrevPage={session.prevPage}
              onNextPage={session.nextPage}
            />
          ) : null}
        </>
      )}
    </TaskShell>
  );
}
